// AppWithRouter.js - URL-based routing implementation
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  memo,
  lazy,
  Suspense,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";

// Firebase imports
import { signOutUser } from "./auth";
import { auth, db } from "./firebase";
import apiService from "./apiService";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  increment,
} from "firebase/firestore";


// Components
import Navigation from "./components/Navigation";
import RouteTransitionManager from "./components/RouteTransitionManager";
import GenerationQueue from "./components/GenerationQueue";
import FailedJobBanner from "./components/FailedJobBanner";
import FirebaseChecker from "./components/FirebaseChecker";
import { OPT_IN_REQUIRED } from "./components/WelcomeModal";
import EmailOptInGate from "./components/EmailOptInGate";
import CookieBanner from "./components/CookieBanner";
import GlobalFooter from "./components/GlobalFooter";

// Eagerly loaded — home page and primary nav tabs
import HomePage from "./pages/HomePage";
// Eager: this renders INSTEAD of the app for an unverified account, and a lazy chunk would
// show a blank frame at the moment we owe the person an explanation.
import VerifyEmailWall from "./components/VerifyEmailWall";
import ExplorePage from "./pages/ExplorePage";
import WorkspacePage from "./pages/WorkspacePage";

// Memoized variants for the always-mounted pages. Paired with a referentially
// stable commonProps (below), these skip re-rendering when nothing they consume
// actually changed — e.g. unrelated AppContent state updates no longer cascade.
const MemoHomePage = memo(HomePage);
const MemoExplorePage = memo(ExplorePage);
const MemoWorkspacePage = memo(WorkspacePage);

// Hooks
import { useCredits } from "./hooks/useCredits";
import { useApiWithQueue } from "./hooks/useApiWithQueue";
import { useGenerationQueue } from "./hooks/useGenerationQueue";
import { useHostAccount } from "./hooks/useHostAccount";
import { useGoogleOneTap } from "./hooks/useGoogleOneTap";
import useEv from "./hooks/useEv";

// Utilities
import { preloadSectionThumbnails, preloadThumbnails, storageUrl } from "./utils/thumbnailPreloader";
import { PAGE_ROUTES } from "./utils/constants";

import { AuthProvider, useAuth } from "./AuthContext";
import SiteDownNotice, { useSiteStatus } from "./components/SiteDownNotice";
import { LoginGateProvider, useLoginGate } from "./components/LoginGateProvider";
import LockedVideo from "./components/LockedVideo";
import { useT } from "./i18n/useT";

// Pages an anonymous visitor may open. Anything not listed is account-shaped — their own
// library, settings, billing, and the catalogue — and bounces home.
//
// Watching is NOT gated here: a shared /video/ link plays for anyone, and the account is asked
// for at the next action instead. Explore and Search ARE account-only: they used to be listed
// as public and then covered by a full-screen gate, which is a wall wearing a modal's clothes.
// A bounce is the honest version of the same rule, and the nav asks in place before it ever
// gets here (see Navigation's account-only handling).
const PUBLIC_PATHS = new Set([
  "/",
  // The marketing page, and the place sign-out sends people. It was NOT listed here, so a
  // signed-out visitor who landed on it was treated as trespassing: held on a PageLoader and
  // then bounced to "/". It is the most public page in the product.
  "/landing",
  "/contact",
  "/api",
  "/passwordless",
  // The per-caption links. Public by definition: whoever clicks one has no account yet.
  "/s",
  // Clicked from an inbox, frequently in a browser that has never seen this app. Treating it
  // as private bounces the confirmation link to "/" and the code is spent for nothing.
  "/verify",
]);
// "/create" was here until 2026-09-14, which meant an anonymous visitor reached the full
// HomePage — composer, model picker, popular lessons — and could spend ANONYMOUS_DAILY_LIMIT
// on a real generation without ever making an account. Anonymous access is now landing plus
// shared video links only. ANONYMOUS_DAILY_LIMIT stays enforced server-side as defence in
// depth; nothing in the UI can reach it any more.
const PUBLIC_PATH_PREFIXES = ["/ref/", "/video/", "/upgrade-preview", "/s/"];

const isPublicPath = (path) =>
  PUBLIC_PATHS.has(path) || PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));

// Lazy load — everything not on the critical path
// LandingPage is routed again at /landing as of 2026-09-10 (it had been kept on disk but
// deliberately unimported since the open-access change). Lazy, so signed-in traffic never pays
// for the marketing bundle — only someone who actually asks for /landing downloads it.
const LandingPage = lazy(() => import("./pages/LandingPage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const PasswordlessPage = lazy(() => import("./pages/PasswordlessPage"));
// Eager, not lazy: this renders while the oobCode is being applied, and a chunk fetch on a
// cold cache is dead time on the one screen that must feel instant and trustworthy.
import VerifyEmailAction from "./components/VerifyEmailAction";
import { sourceFromLocation, rememberSource } from "./utils/acquisition";

// RUN AT MODULE LOAD, not in an effect. /s/<slug> immediately redirects to "/", and an effect
// on a component that is about to unmount is a race with that redirect. Reading it here means
// the slug is banked before any router or auth code gets a chance to move the URL.
rememberSource(sourceFromLocation(window.location.pathname, window.location.search));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const PurchaseCreditsPage = lazy(() => import("./pages/PurchaseCreditsPage"));
const PurchaseSuccessPage = lazy(() => import("./pages/PurchaseSuccessPage"));
const CourseDetailPage = lazy(() => import("./pages/CourseDetailPage"));
const VideoPlayerPage = lazy(() => import("./pages/VideoPlayerPage"));
const AdminPage = lazy(() => import("./pages/admin/AdminPage"));
const ReelEditorPage = lazy(() => import("./pages/admin/marketing/reels/ReelEditorPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const ApiPage = lazy(() => import("./pages/ApiPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const LectureSession = lazy(() => import("./lecture/LectureSession"));
const HomeworkSession = lazy(() => import("./homework/HomeworkSession"));
const SubscriptionModalPreview = import.meta.env.DEV
  ? lazy(() => import("./components/SubscriptionModal"))
  : null;
// The real one, mounted app-wide (see the `magi:open-subscribe` listener). Separate from the
// DEV preview above, which exists only to render the modal at a URL.
const SubscriptionModal = lazy(() => import("./components/SubscriptionModal"));

// Loading component with top progress bar
const TopLoadingBar = ({ progress = 70 }) => {
  /* MARK THE DOCUMENT WHILE A ROUTE IS STILL RESOLVING.
     This bar IS the Suspense fallback, so while it is mounted #root holds nothing else — and
     the legal footer, which lives in <body> and is now pinned to the bottom of the viewport,
     sits alone at the bottom of a blank screen. Reported on sign-out, where navigating to the
     lazy /landing chunk leaves exactly that gap. A legal row under an empty page reads as a
     rendering fault; it belongs under content.

     A class on <html> rather than prop-drilling: the footer is a portal into <body>, several
     trees away, and this is the same shape as --magi-sidebar and --magi-footer-h. Counted
     rather than a boolean, so two overlapping loaders cannot have the first to unmount clear
     the flag while the second is still showing. */
  useLayoutEffect(() => {
    const root = document.documentElement;
    const n = Number(root.dataset.routeLoading || 0) + 1;
    root.dataset.routeLoading = String(n);
    root.classList.add("route-loading");
    return () => {
      const left = Number(root.dataset.routeLoading || 1) - 1;
      root.dataset.routeLoading = String(Math.max(left, 0));
      if (left <= 0) {
        delete root.dataset.routeLoading;
        root.classList.remove("route-loading");
      }
    };
  }, []);

  return (
    <div
      className="fixed top-0 left-0 right-0 h-1 z-[99999]"
      style={{ pointerEvents: "none" }}
    >
      <div
        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg shadow-blue-500/50 transition-all duration-300 ease-out"
        style={{
          width: `${progress}%`,
          transformOrigin: "left",
        }}
      />
    </div>
  );
};

// Loading component for lazy-loaded routes
const PageLoader = () => <TopLoadingBar progress={70} />;

// Protected Route Component — requires a real (non-anonymous) Google account
const ProtectedRoute = ({ children, user }) => {
  if (user === null) {
    // Still loading
    return <TopLoadingBar progress={50} />;
  }

  return user ? children : null;
};

/* THE DEMO PLAYER USED TO LIVE HERE — one <video> parked in a hidden div at module scope,
   adopted into the home page's chip by DOM manipulation and reachable through
   `window.__persistentVideoRef`. It is gone; HomePage renders its own pair (see
   `hooks/useDemoReel`), as ordinary React with ordinary refs.

   IT HAD TWO JUSTIFICATIONS AND THE POSTER RETIRED BOTH. It buffered behind the splash, which
   stopped mattering once the chip paints a cover instantly; and it survived route changes,
   which is worth little when returning to Home re-reads from the HTTP cache.

   Against that it cost a window global, DOM surgery across two modules, and a real bug: this
   component was rendered at TWO tree positions — once as the whole return while `authLoading`,
   once inside the shell — so React destroyed and recreated the element the moment auth
   resolved, throwing away the very stream the parking existed to start early. */

// Navigation wrapper component to handle navigation logic
const NavigationWrapper = ({
  children,
  user,
  userData,
  onSignOut,
  credits,
  creditsLoading,
  isOutOfCredits,
  dailyUsage,
  setCurrentPage,
  getCurrentPage,
  preloadedData,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      // Two corpora, two destinations: searching from the Workspace filters YOUR videos in
      // place; anywhere else searches the whole catalogue on Explore. Results render in the
      // page you are already on rather than on a separate /search page to back out of.
      // Session pages count as "yours" as well: you got there from your own library, and the
      // bar reads "Search your videos" there, so it must not land on the public catalogue.
      const page = getCurrentPage();
      const onWorkspace = page === "workspace" || page === "study-session";
      const path = onWorkspace
        ? `/workspace?q=${encodeURIComponent(query)}`
        : `/explore?q=${encodeURIComponent(query)}`;
      if (window.__navigateWithTransition) {
        window.__navigateWithTransition(path);
      } else {
        navigate(path);
      }
    }
  };

  return (
    <>
      <Navigation
        currentPage={getCurrentPage()}
        user={user}
        userData={userData}
        setCurrentPage={setCurrentPage}
        searchQuery={searchQuery}
        setSearchQuery={handleSearch}
        showSearch={true}
        credits={credits}
        creditsLoading={creditsLoading}
        isOutOfCredits={isOutOfCredits}
        dailyUsage={dailyUsage}
        onSignOut={onSignOut}
        preloadedData={preloadedData}
      />
      <div className="flex-1">{children}</div>
    </>
  );
};

// Main App Component
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Lazy-mount tracking — mounts on first visit, stays mounted after
  const workspaceMountedRef = useRef(false);
  const exploreMountedRef = useRef(false);
  // Idle pre-mount: mount key pages in background after initial load settles
  const [idleMounted, setIdleMounted] = useState(false);

  /* WARM THE WORKSPACE BEFORE IT IS ASKED FOR. It is always mounted but parked at height:0
     with overflow hidden, which means nothing in it has ever been PAINTED and its row
     observers have never intersected anything — so only the two eager rows exist. Revealing
     it therefore does all of that work in the frames right after the reveal: first paint of
     every visible card, then a burst of IntersectionObserver callbacks mounting the rest of
     the rows and requesting their thumbnails. Landing that on the same frames as the
     composer morph is what is left of the stutter.
     Home fires `magi:warm-workspace` as soon as you touch the composer — well before you
     press enter — and from then on the page is laid out and painted at viewport size behind
     everything, at zero opacity. The work is the same; it just happens while you are typing
     instead of while something is animating. */
  const [workspaceWarm, setWorkspaceWarm] = useState(false);
  useEffect(() => {
    if (workspaceWarm) return;
    const warm = () => setWorkspaceWarm(true);
    window.addEventListener("magi:warm-workspace", warm);
    return () => window.removeEventListener("magi:warm-workspace", warm);
  }, [workspaceWarm]);
  /* A MISSED DROP MUST DO NOTHING, not navigate away. The composer accepts dragged documents
     now, and a drag that lands anywhere else hits the browser's default: it leaves the SPA and
     opens the file, which from the user's side is indistinguishable from the app crashing —
     and it happens after they have already committed to the gesture. Swallowing dragover/drop
     at the window makes a miss a silent no-op.

     This CANNOT swallow the composer's own drops: those handlers call preventDefault and the
     listeners here are on `window`, so by the time the event reaches this it has already been
     handled and defaultPrevented is true. The guard only fires for events nothing else claimed.

     Not `capture`, for exactly that reason — capturing would run this BEFORE the composer and
     break the feature it exists to protect. */
  useEffect(() => {
    const swallow = (e) => {
      if (e.defaultPrevented) return;
      if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
      e.preventDefault();
      if (e.type === "dragover" && e.dataTransfer) e.dataTransfer.dropEffect = "none";
    };
    window.addEventListener("dragover", swallow);
    window.addEventListener("drop", swallow);
    return () => {
      window.removeEventListener("dragover", swallow);
      window.removeEventListener("drop", swallow);
    };
  }, []);

  useEffect(() => {
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 3000));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = schedule(() => setIdleMounted(true), { timeout: 5000 });
    return () => cancel(id);
  }, []);

  // Auth state — single source of truth from AuthContext
  const { user, userData, authLoading } = useAuth();
  // Null while the site is serving, and null for admins always. Polls, so the site coming
  // back up clears the notice without anyone having to refresh.
  const siteDown = useSiteStatus(user, !authLoading);
  const t = useT();
  const { openLoginGate } = useLoginGate();

  // Google One Tap sign-in prompt (top-right corner) for non-logged-in users
  useGoogleOneTap(user, authLoading);

  // Anonymous visitors are IN the app, not outside it. Watching is free and a shared link
  // plays; the ask is at the ACTION that needs an account. Account-shaped pages — their own
  // library, settings, billing, and the catalogue — bounce home, where the landing page makes
  // its own case. The nav asks in place before anyone gets here; this is the direct-URL path.
  useEffect(() => {
    if (authLoading) return;
    if (user && !user.isAnonymous) return;

    const path = location.pathname;
    if (isPublicPath(path)) return;

    // WAIT before believing it. Firebase reports a null user for a moment whenever a token
    // refresh hiccups — a sleeping laptop, a network blip — even for a visitor who is signed
    // in and stays signed in. Acting on that instantly threw them off the page they were on
    // and put a sign-in prompt over their own logged-in session. The timer is cancelled the
    // moment a real user reappears, so a transient null now does nothing at all.
    const settle = setTimeout(() => {
      // "/" is the LANDING PAGE for a signed-out visitor, and it makes its own case with its
      // own sign-in — so this is a bare navigate with no second prompt over it. Video links do
      // NOT reach here: "/video/" is public, and watching needs no account at all.
      navigate("/", { replace: true, state: { from: path } });
    }, 2500);
    return () => clearTimeout(settle);
  }, [user, authLoading, location.pathname]);

  // Preloaded data cache
  const [preloadedData, setPreloadedData] = useState({
    courses: null,
    recentVideos: null,
    explore: null,
  });
  const [dataReady, setDataReady] = useState(false);
  const [exploreDataReady, setExploreDataReady] = useState(false);

  // Credits - only called at app level, skip for anonymous users
  const {
    credits,
    dailyUsage,
    loading: creditsLoading,
    refreshCredits,
    refreshDailyUsage,
  } = useCredits(user ? user.uid : null, userData);

  // Host account status
  const { isHost } = useHostAccount(user);

  // Page view tracking
  useEv(user?.uid || null, user?.email || null);

  // Debug state
  // Removed showDebug state - DebugPanel component no longer exists

  // Generation queue with completion callback
  // Track current course being viewed for targeted refresh
  const [currentCourseId, setCurrentCourseId] = useState(null);
  const [courseRefreshCallback, setCourseRefreshCallback] = useState(null);

  // Upgrade entry points (the Premium banner, the profile menu's Upgrade item) open the
  // subscribe modal here rather than navigating to /credits — a full page swap loses the
  // context you were upgrading FROM.
  const [showSubscribe, setShowSubscribe] = useState(false);
  useEffect(() => {
    const open = () => setShowSubscribe(true);
    window.addEventListener("magi:open-subscribe", open);
    return () => window.removeEventListener("magi:open-subscribe", open);
  }, []);

  const generationQueue = useGenerationQueue({
    /* WHOSE QUEUE. Without this the hook kept one queue for the DEVICE: signing out and into
       another account is a route change, not a reload, so the previous account's cards stayed
       in memory — and a reload restored them from a localStorage key that had no uid in it. */
    userId: user?.uid || null,
    onJobCompleted: async (completedJob) => {
      console.log("🔄 Job completed, refreshing user data...", completedJob);

      // Refresh credits immediately since a job used credits
      refreshCredits();

      // Retry mechanism with verification to ensure data has propagated
      if (user?.uid) {
        let retries = 0;
        let newData = null;
        let foundCompleted = false;

        while (retries < 3 && !foundCompleted) {
          // Wait before fetching (0.5s, 1s, 2s)
          const delay = 500 * Math.pow(2, retries);
          await new Promise((resolve) => setTimeout(resolve, delay));

          // Fetch the latest data
          newData = await preloadUserData(user.uid);

          // Check if the completed job is in the fetched data with completed status AND thumbnail
          if (newData) {
            const allVideos = newData.recentVideos || [];
            const foundVideo = allVideos.find(
              (v) =>
                v.job_id === completedJob.id &&
                v.status === "completed" &&
                (v.thumbnail_url || v.thumbnail_storage_path), // Also verify thumbnail data
            );

            if (foundVideo) {
              console.log(
                `✅ Found completed video with thumbnail after ${retries + 1} attempt(s)`,
              );
              // Pass thumbnail data to the job so the generating card uses the same cache key
              generationQueue.updateJobThumbnail(completedJob.id, {
                thumbnail_storage_path: foundVideo.thumbnail_storage_path || null,
                thumbnail_url: foundVideo.thumbnail_url || null,
              });
              // Preload thumbnail blob so the generating card shows it instantly
              try {
                await preloadThumbnails([foundVideo], { priority: 'high', concurrency: 1 });
              } catch (thumbErr) {
                console.warn('Thumbnail preload failed, card will load from server:', thumbErr.message);
              }
              foundCompleted = true;
            } else {
              // Check if video exists but without thumbnail
              const videoWithoutThumbnail = allVideos.find(
                (v) => v.job_id === completedJob.id && v.status === "completed",
              );

              if (videoWithoutThumbnail) {
                console.log(
                  `⏳ Video found but thumbnail not ready, retry ${retries + 1}/3...`,
                );
              } else {
                console.log(
                  `⏳ Video not yet visible, retry ${retries + 1}/3...`,
                );
              }

              retries++;
            }
          } else {
            retries++;
          }
        }

        // If thumbnail wasn't found in the initial retries, keep polling in background
        if (!foundCompleted && user?.uid) {
          const bgPoll = async () => {
            for (let i = 0; i < 6; i++) {
              await new Promise(r => setTimeout(r, 3000));
              const data = await preloadUserData(user.uid);
              const vid = (data?.recentVideos || []).find(
                v => v.job_id === completedJob.id && v.status === "completed" &&
                  (v.thumbnail_url || v.thumbnail_storage_path)
              );
              if (vid) {
                generationQueue.updateJobThumbnail(completedJob.id, {
                  thumbnail_storage_path: vid.thumbnail_storage_path || null,
                  thumbnail_url: vid.thumbnail_url || null,
                });
                try { await preloadThumbnails([vid], { priority: 'high', concurrency: 1 }); } catch {}
                setPreloadedData(prev => ({
                  ...prev,
                  recentVideos: data.recentVideos || prev.recentVideos,
                  _refreshTimestamp: Date.now(),
                }));
                break;
              }
            }
          };
          bgPoll();
        }

        // Check if this is a lesson video completion
        if (
          completedJob.type === "lesson_video" &&
          completedJob.metadata?.courseId
        ) {
          console.log(
            "📚 Lesson video completed for course:",
            completedJob.metadata.courseId,
          );

          // If we're currently viewing this course, trigger a refresh
          if (
            courseRefreshCallback &&
            completedJob.metadata.courseId === currentCourseId
          ) {
            console.log("🔄 Refreshing current course data...");
            // Add small delay to ensure database update has propagated
            setTimeout(() => {
              courseRefreshCallback();
            }, 500);
          }
        }

        // Update state with fetched data (even if we didn't find the specific video)
        if (newData) {
          setPreloadedData((prevData) => ({
            ...prevData,
            courses: newData.courses || prevData.courses,
            recentVideos: newData.recentVideos || prevData.recentVideos,
            _refreshTimestamp: Date.now(), // Force React to detect change
          }));

          // Also refresh explore data for HomePage
          await preloadExploreData();
        }
      }
    },
  });

  // Backfill thumbnails for completed jobs that missed onJobCompleted (e.g. tab was closed)
  useEffect(() => {
    if (!user?.uid || !generationQueue.queue.length) return;
    const missing = generationQueue.queue.filter(
      j => j.state === 'completed' && j.type !== 'project' && !j.thumbnail_storage_path && !j.thumbnail_url
    );
    if (!missing.length) return;
    (async () => {
      const data = await preloadUserData(user.uid);
      for (const job of missing) {
        const vid = (data?.recentVideos || []).find(
          v => v.job_id === job.id && (v.thumbnail_url || v.thumbnail_storage_path)
        );
        if (vid) {
          generationQueue.updateJobThumbnail(job.id, {
            thumbnail_storage_path: vid.thumbnail_storage_path || null,
            thumbnail_url: vid.thumbnail_url || null,
          });
          try { await preloadThumbnails([vid], { priority: 'high', concurrency: 1 }); } catch {}
        }
      }
    })();
  }, [user?.uid]); // Run once on login/mount

  // Enhanced API with queue management
  const {
    generateVideo,
    solveProblem: generateProblemSolution, // Rename to match expected prop
    solvePdfProblems,
    generateCourse,
    generateCourseWithQueue,
    generateLessonWithQueue,
    generateAllLessonsWithQueue,
    generateUnified,
    generateUnifiedBatch,
    checkGenerationStatus,
  } = useApiWithQueue(generationQueue);

  // Preload all user data
  const preloadUserData = useCallback(async (userId) => {
    if (!userId) return null;

    try {
      console.log("Preloading user data...");

      // Fetch courses and recent videos in parallel (apiService handles auth)
      // Also prefetch study sessions into sessionStorage (non-blocking)
      const [coursesData, videosData] = await Promise.all([
        apiService.getUserContent(userId, "courses").catch(() => null),
        apiService.getUserContent(userId, "videos", 20).catch(() => null),
      ]);
      // Study sessions prefetch disabled — feature hidden from production

      const newPreloadedData = {};

      if (coursesData) {
        newPreloadedData.courses = coursesData.courses || [];
        console.log("Preloaded courses:", newPreloadedData.courses.length);
      }

      if (videosData) {
        newPreloadedData.recentVideos = (videosData.videos || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
        console.log(
          "Preloaded recent videos:",
          newPreloadedData.recentVideos.length,
        );
      }

      setPreloadedData((prev) => ({ ...prev, ...newPreloadedData }));
      setDataReady(true);
      console.log("All user data preloaded successfully");

      // Preload thumbnails in the background
      Promise.all([
        preloadSectionThumbnails("courses", newPreloadedData),
        preloadSectionThumbnails("recentVideos", newPreloadedData),
      ])
        .then((results) => {
          const totalLoaded = results.reduce(
            (sum, r) => sum + (r?.loaded || 0),
            0,
          );
          const totalCached = results.reduce(
            (sum, r) => sum + (r?.cached || 0),
            0,
          );
          console.log(
            `Thumbnails preloaded: ${totalLoaded} new, ${totalCached} cached`,
          );
        })
        .catch((error) => {
          console.warn("Thumbnail preload error:", error);
        });

      // Return the data so onJobCompleted can use it
      return newPreloadedData;
    } catch (error) {
      console.error("Error preloading user data:", error);
      // Even if some data fails, mark as ready to prevent infinite loading
      setDataReady(true);
      return null;
    }
  }, []);

  // Preload explore page data
  const preloadExploreData = useCallback(async () => {
    try {
      console.log("Preloading explore data...");

      // Load curated explore data (semantic categories, moderation, hero)
      const curatedData = await apiService.getExploreCurated().catch((error) => {
        console.error("Failed to fetch curated explore:", error);
        return null;
      });

      const exploreData = curatedData || { hero: null, categories: [] };
      console.log("Preloaded curated explore:", exploreData.categories?.length, "categories");

      setPreloadedData((prev) => ({
        ...prev,
        explore: exploreData,
        _exploreTimestamp: Date.now(),
      }));
      setExploreDataReady(true);
    } catch (error) {
      console.error("Error preloading explore data:", error);
      // Even if some data fails, mark as ready to prevent infinite loading
      setExploreDataReady(true);
    }
  }, []);

  // Out of credits check — subscribers use monthly cap, free tier uses daily limit
  const isSubscriber =
    credits?.subscription_plan && credits.subscription_plan !== "free";
  const isOutOfCredits = isSubscriber
    ? credits.used_this_month >= credits.monthly_cap
    : dailyUsage.remaining === 0;

  // Navigation function (moved from NavigationWrapper)
  const setCurrentPage = (page, params = {}) => {
    console.log("🔄 setCurrentPage called with:", page, params);
    console.log("📍 Current location:", location.pathname);
    console.log("🗺️ Looking for route in routeMap for page:", page);

    // Static routes come from the shared table (utils/constants) so the sidebar's hrefs and
    // this navigation can never disagree; the two parameterised ones live here because they
    // need arguments a static table cannot hold.
    const routeMap = {
      ...PAGE_ROUTES,
      search: `/search${params.query ? `?q=${params.query}` : ""}`,
      "video-player": `/video/${params.videoId || "view"}`,
    };

    const route = routeMap[page] || "/";
    console.log("🔄 Navigating to route:", route, "for page:", page);
    if (!routeMap[page]) {
      console.warn("⚠️ No route found for page:", page, "- defaulting to /");
    }

    // Use transition navigation if available
    if (window.__navigateWithTransition) {
      window.__navigateWithTransition(route, { state: params });
    } else {
      navigate(route, { state: params });
    }
  };

  // Same rule LoginGateProvider uses. Anonymous is a visitor: they have no account to come
  // back to, so the front door is still a pitch rather than the app.
  const isSignedOut = !authLoading && (!user || user.isAnonymous);

  // Determine current page from route
  const getCurrentPage = () => {
    const path = location.pathname;

    // "/" is the LANDING PAGE for signed-out visitors and the app for everyone else.
    //
    // `isSignedOut` matches LoginGateProvider's definition EXACTLY — anonymous counts as
    // signed out — so the two surfaces can never disagree about who is a visitor. While
    // authLoading is true we return "home": the app renders hidden and nothing flashes,
    // whereas guessing "landing" would show a marketing page to a signed-in user for a frame.
    if (path === "/landing") return "landing";
    if (path === "/") return isSignedOut ? "landing" : "home";
    // DEV ONLY — the upgrade modal otherwise only appears once a user hits their limit,
    // which makes it awkward to review. Stripped from production builds.
    if (path === "/upgrade-preview" && import.meta.env.DEV) return "upgrade-preview";
    if (path.startsWith("/ref/")) return "ref";
    if (path === "/passwordless") return "passwordless";
    if (path === "/verify") return "verify";
    if (path.startsWith("/workspace/session/")) return "study-session";
    if (path.startsWith("/study/session/")) return "study-session";
    if (path.startsWith("/homework/session/")) return "study-session";
    if (path === "/workspace") return "workspace";
    if (path === "/explore") return "explore";
    if (path.startsWith("/create")) return "home";
    if (path === "/credits" || path === "/credits/success") return "credits";
    if (path === "/settings") return "settings";
    if (path === "/billing") return "billing";
    if (path === "/api") return "api";
    if (path === "/contact") return "contact";
    // startsWith, not equality: /admin/reel/:jobId fell through to the "home" default
    // below, and the always-mounted HomePage then rendered VISIBLE on top of the routed
    // editor — which looks exactly like being bounced back to the home page. Same trap the
    // /course/ line below documents.
    if (path.startsWith("/admin")) return "admin";
    if (path.startsWith("/video/")) return "video-player";
    // Without this the fall-through below returns "home", and the always-mounted
    // HomePage stays visible ON TOP of the routed course page.
    if (path.startsWith("/course/")) return "course";
    if (path === "/search") return "search";
    // Legacy redirects
    if (path === "/projects" || path.startsWith("/library")) return "workspace";

    return "home";
  };

  // Problem solving state (needed by ProblemSolverPage)
  const [problem, setProblem] = useState({
    isGenerating: false,
    progress: 0,
    currentStep: "",
    error: null,
    generatedVideo: null,
    isQueued: false,
  });

  // Video generation state (needed by LessonGeneratorPage)
  const [video, setVideo] = useState({
    isGenerating: false,
    progress: 0,
    currentStep: "",
    error: null,
    generatedVideo: null,
    jobId: null,
    isQueued: false,
  });

  // Connection status - not used but keeping state for compatibility
  const [isConnected] = useState(true);

  // Credits helper function — subscribers use monthly cap, free users use daily limit
  const hasCredits = (amount) => {
    if (isSubscriber) {
      return credits.monthly_cap - credits.used_this_month >= amount;
    }
    // Free logged-in users: check daily usage
    return dailyUsage.remaining >= amount;
  };

  // Problem solving wrapper function
  const solveProblem = async (problemText, userId, options = {}) => {
    try {
      // Start generation
      setProblem((prev) => ({
        ...prev,
        isGenerating: true,
        error: null,
        progress: 0,
      }));

      // generateProblemSolution is already the queue-enhanced version from useApiWithQueue
      const result = await generateProblemSolution(
        problemText,
        userId,
        options,
      );

      // Successfully queued - show queued state instead of progress
      setProblem((prev) => ({
        ...prev,
        isGenerating: false,
        isQueued: true,
      }));

      // Don't track progress here - let the queue handle it
      return result;
    } catch (error) {
      setProblem((prev) => ({
        ...prev,
        isGenerating: false,
        isQueued: false,
        error:
          "Something went wrong. Please try again.",
        progress: 0,
      }));
      throw error;
    }
  };

  // Reset problem solving state
  const resetProblemSolving = () => {
    setProblem({
      isGenerating: false,
      progress: 0,
      currentStep: "",
      error: null,
      generatedVideo: null,
      isQueued: false,
    });
  };

  // Video generation wrapper function
  const generateVideoWrapper = async (formData, userId) => {
    try {
      // Start generation
      setVideo((prev) => ({
        ...prev,
        isGenerating: true,
        error: null,
        progress: 0,
      }));

      // generateVideo is already the queue-enhanced version from useApiWithQueue
      const result = await generateVideo(formData, userId);

      // Successfully queued - show queued state instead of progress
      setVideo((prev) => ({
        ...prev,
        isGenerating: false,
        isQueued: true,
        jobId: result?.jobId,
      }));

      // Don't track progress here - let the queue handle it
      return result;
    } catch (error) {
      setVideo((prev) => ({
        ...prev,
        isGenerating: false,
        isQueued: false,
        error:
          "Something went wrong. Please try again.",
        progress: 0,
      }));
      throw error;
    }
  };

  // Reset video generation state
  const resetVideoGeneration = () => {
    setVideo({
      isGenerating: false,
      progress: 0,
      currentStep: "",
      error: null,
      generatedVideo: null,
      jobId: null,
      isQueued: false,
    });
  };

  // Preload user data when user signs in
  // Auth state comes from AuthContext (single listener)
  useEffect(() => {
    if (!user || user.isAnonymous) return;

    preloadUserData(user.uid).catch((err) =>
      console.error("Error preloading:", err),
    );
  }, [user, preloadUserData]);

  // Preload explore data on app start
  useEffect(() => {
    preloadExploreData();
  }, [preloadExploreData]);

  // Listen for session expired events
  // A session expiring is NEVER caused by idling. Firebase persists the refresh token in
  // IndexedDB (browserLocalPersistence, the default — see firebase.js) and mints new ID tokens
  // silently forever; sleeping, tab-switching and closing the browser do not end a session.
  // This only fires once apiService has re-asked Firebase and confirmed the refresh token is
  // genuinely dead — a password change, a revoked account, a deleted user.
  //
  // It used to sign the visitor out and navigate to "/". That was backwards: the prompt
  // MANUFACTURED the logout it claimed had already happened, and it fired off any flaky token
  // refresh, so an idle tab could destroy a perfectly valid session on a single click.
  // Now the sign-out only clears a credential we have confirmed is unusable, and it happens
  // where the visitor already is — no navigation, so they keep the page they were on.
  useEffect(() => {
    const handleSessionExpired = async () => {
      console.log("🚨 Session expired (confirmed) — clearing the dead credential");
      await signOutUser();
      openLoginGate({ title: t("session_expired"), subtitle: t("session_expired_desc") });
    };

    window.addEventListener("session-expired", handleSessionExpired);
    return () =>
      window.removeEventListener("session-expired", handleSessionExpired);
  }, [openLoginGate, t]);

  // Handle sign out
  const handleSignOut = async () => {
    await signOutUser();
    // "/landing", NOT "/". Both end up on the landing page, but "/" gets there by ASKING WHO
    // YOU ARE — getCurrentPage() returns `isSignedOut ? "landing" : "home"` — and for the
    // frames between signOut() resolving and AuthContext's listener clearing `user`, the
    // answer is still "signed in". So signing out flashed the logged-in home page on the way
    // out. "/landing" is unconditional in getCurrentPage(), so there is no window in which it
    // can resolve to anything else.
    navigate("/landing");
  };

  // Stable handler identities. These functions are recreated on every render
  // (inline consts + values from useApiWithQueue), which previously invalidated
  // the commonProps memo every render and cascaded re-renders into all three
  // always-mounted pages. We delegate through a ref so the exposed identities are
  // permanently stable while always calling the latest closures (no stale state).
  const handlersRef = useRef(null);
  handlersRef.current = {
    preloadUserData,
    preloadExploreData,
    setCurrentPage,
    setCurrentCourseId,
    setCourseRefreshCallback,
    generateVideo,
    generateProblemSolution,
    generateCourse,
    generateCourseWithQueue,
    generateLessonWithQueue,
    generateAllLessonsWithQueue,
    generateUnified,
    generateUnifiedBatch,
    checkGenerationStatus,
    solveProblem,
    solvePdfProblems,
    resetProblemSolving,
    hasCredits,
    refreshDailyUsage,
    generateVideoWrapper,
    resetVideoGeneration,
  };
  const stableHandlers = useMemo(() => {
    const wrap = (name) => (...args) => handlersRef.current[name](...args);
    return {
      preloadUserData: wrap("preloadUserData"),
      preloadExploreData: wrap("preloadExploreData"),
      setCurrentPage: wrap("setCurrentPage"),
      setCurrentCourseId: wrap("setCurrentCourseId"),
      setCourseRefreshCallback: wrap("setCourseRefreshCallback"),
      generateVideo: wrap("generateVideo"),
      generateProblemSolution: wrap("generateProblemSolution"),
      generateCourse: wrap("generateCourse"),
      generateCourseWithQueue: wrap("generateCourseWithQueue"),
      generateLessonWithQueue: wrap("generateLessonWithQueue"),
      generateAllLessonsWithQueue: wrap("generateAllLessonsWithQueue"),
      generateUnified: wrap("generateUnified"),
      generateUnifiedBatch: wrap("generateUnifiedBatch"),
      checkGenerationStatus: wrap("checkGenerationStatus"),
      solveProblem: wrap("solveProblem"),
      solvePdfProblems: wrap("solvePdfProblems"),
      resetProblemSolving: wrap("resetProblemSolving"),
      hasCredits: wrap("hasCredits"),
      refreshDailyUsage: wrap("refreshDailyUsage"),
      generateVideoWrapper: wrap("generateVideoWrapper"),
      resetVideoGeneration: wrap("resetVideoGeneration"),
    };
  }, []);

  // Props to pass to all pages. Deps are data values only — the (now stable)
  // handlers are spread in from stableHandlers so they never invalidate the memo.
  const commonProps = useMemo(
    () => ({
      user,
      userData,
      credits,
      creditsLoading,
      isOutOfCredits,
      isHost,
      generationQueue,
      preloadedData,
      dataReady,
      exploreDataReady,
      problem,
      isConnected,
      dailyUsage,
      video,
      ...stableHandlers,
    }),
    [
      user,
      userData,
      credits,
      creditsLoading,
      isOutOfCredits,
      isHost,
      generationQueue,
      preloadedData,
      dataReady,
      exploreDataReady,
      problem,
      isConnected,
      dailyUsage,
      video,
      stableHandlers,
    ],
  );

  // Dismiss splash screen once auth resolves, animation finishes, and video is ready
  useEffect(() => {
    if (authLoading) return;

    /* ONE-WAY LATCH FOR THE LEGAL FOOTER, which is display:none until <html> carries this.
       The footer is a portal into <body>, so it mounts the instant App stops returning early
       — while the splash is still counting down its 2950ms — and is then revealed on its own
       terms rather than with the page. That is what put "Notice at collection" on screen
       before anything else had arrived. Setting it here means the row appears exactly when
       the rest of the page does.

       SET ON EVERY PATH, not just the ones that show a splash. /admin deletes #splash in
       index.html before the bundle loads, so returning early below without setting this would
       leave the flag off for the whole session — and a later navigation to a page that DOES
       have a footer would render without one. */
    const markAppReady = () => { document.documentElement.dataset.appReady = ''; };

    const splash = document.getElementById('splash');
    if (!splash) { markAppReady(); return; }

    const minDisplay = 2950; // ms — covers letter stagger + hat drop animation
    const elapsed = performance.now();
    const minDelay = Math.max(0, minDisplay - elapsed);

    const dismissSplash = () => {
      // Before the fade, not after it: the footer should be part of what the fade reveals.
      markAppReady();
      splash.style.opacity = '0';
      splash.addEventListener('transitionend', () => splash.remove());
    };

    /* THE TIMER ALONE. This used to also wait on the parked demo video's `canplay` (capped
       at 5s), so the splash could outlast its own animation by seconds on a slow connection.
       There is nothing to wait for any more: the chip paints a lesson's cover as its poster
       before a byte of video arrives, so the page behind the splash is never empty. Dropping
       the condition makes the splash deterministic, and can only make it shorter. */
    setTimeout(dismissSplash, minDelay);
  }, [authLoading]);

  // Still loading auth — the splash covers this.
  if (authLoading) return null;
  // Bouncing takes 2.5s (the guard above deliberately waits out a transient null user), and
  // for those 2.5s an account-only page would be on screen and readable. Hold the frame
  // instead. This is not the gate coming back: nothing is drawn over anything, the page simply
  // does not render for someone who is on their way off it.
  if (!authLoading && (!user || user.isAnonymous) && !isPublicPath(location.pathname)) {
    return <PageLoader />;
  }

  // EMAIL NOT CONFIRMED — instead of the app, not over it.
  //
  // Firebase signs you in as a side effect of creating the credential, so a fresh email signup
  // is authenticated but unproven. Everything below this line is for people who have proven an
  // address. Placed with the maintenance notice, and for the same reason: nothing behind it
  // mounts or fires calls it is not entitled to make.
  //
  // Google and email-link accounts arrive with emailVerified already true and never see this.
  // /passwordless stays reachable — it IS a confirmation flow, so walling it would lock the
  // door from the inside.
  if (
    !authLoading
    && user
    && !user.isAnonymous
    && user.emailVerified === false
    && (user.providerData || []).some((p) => p?.providerId === "password")
    && location.pathname !== "/passwordless"
    // The wall exists to be removed by this page. Walling it strands anyone who clicks the
    // link in the same tab: the code never gets applied and the wall never lifts.
    && location.pathname !== "/verify"
  ) {
    return <VerifyEmailWall user={user} />;
  }

  // MAINTENANCE / HALT — instead of the app, not over it.
  //
  // Placed after the auth gate so the admin claim has resolved: admins keep working while
  // everything else is refused, which is the entire point of letting /api/admin through the
  // middleware. Placed before every route so no page gets to mount and fire calls that will
  // 503, each producing an error toast contradicting the notice behind it.
  if (siteDown) {
    return <SiteDownNotice status={siteDown} />;
  }

  return (
    <>
      {/* Post-signup marketing consent, in the countries that require it. The age screen
          that used to share this modal has been removed entirely. */}
      {userData && !userData.anonymous
        && userData.email_marketing_opt_in == null
        && OPT_IN_REQUIRED.has(userData.country_code)
        && <EmailOptInGate />}

      {/* Landing & auth pages render outside the main app shell (no nav, no always-mounted pages) */}
      {/* The sign-in gate is mounted by LoginGateProvider — see components/LoginGateProvider.jsx. */}

      {getCurrentPage() === "landing" ? (
        /* THE FRONT DOOR, and it must render OUTSIDE the app shell — no Navigation, no
           sidebar padding, no always-mounted pages. It is a full-bleed marketing page with
           its own sign-in; wrapped in <main> it inherits the nav header and the sidebar
           gutter and stops looking like a landing page at all. This is where it sat the last
           time it was routed. */
        <Suspense fallback={<PageLoader />}>
          <LandingPage />
        </Suspense>
      ) : getCurrentPage() === "upgrade-preview" ? (
        <Suspense fallback={<PageLoader />}>
          <div className="min-h-screen" style={{ background: "#0A0A0B" }}>
            <SubscriptionModalPreview user={user} onClose={() => navigate("/")} />
          </div>
        </Suspense>
      ) : (
      <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-md focus:text-sm focus:font-medium">
        Skip to main content
      </a>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationWrapper
          user={user}
          userData={userData}
          onSignOut={handleSignOut}
          credits={credits}
          creditsLoading={creditsLoading}
          isOutOfCredits={isOutOfCredits}
          dailyUsage={dailyUsage}
          setCurrentPage={setCurrentPage}
          getCurrentPage={getCurrentPage}
          preloadedData={preloadedData}
        >
          <main id="main-content" role="main" className="pt-16 md:pt-0" style={{ paddingLeft: "var(--magi-sidebar, 0px)" }}>
          {/* Always-mounted pages. visibility:hidden keeps images decoded, opacity:0 prevents flash. */}
          <div
            style={
              getCurrentPage() === "home"
                ? undefined
                : {
                    visibility: "hidden",
                    opacity: 0,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 0,
                    overflow: "hidden",
                    pointerEvents: "none",
                  }
            }
          >
            <MemoHomePage {...commonProps} hidden={getCurrentPage() !== "home"} />
          </div>
          {(() => {
            // Same always-mounted pattern as Explore: mounted once, then hidden rather
            // than unmounted, so switching tabs keeps its scroll position and does not refetch.
            const isWorkspace = getCurrentPage() === "workspace";
            if (isWorkspace || idleMounted) workspaceMountedRef.current = true;
            if (!workspaceMountedRef.current) return null;
            return (
              <div
                style={isWorkspace ? undefined : (workspaceWarm
                  /* Warm: real viewport geometry, painted, just invisible and unreachable.
                     `visibility: hidden` and `height: 0` both suppress painting entirely,
                     which is exactly what we are trying to stop doing here — so neither can
                     be used, and opacity + a negative stacking index do the hiding instead. */
                  ? { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, opacity: 0,
                      overflow: "hidden", pointerEvents: "none", zIndex: -1 }
                  : { visibility: "hidden", opacity: 0, position: "absolute", top: 0, left: 0,
                      right: 0, height: 0, overflow: "hidden", pointerEvents: "none" })}
                aria-hidden={isWorkspace ? undefined : true}
              >
                <MemoWorkspacePage {...commonProps} isVisible={isWorkspace} />
              </div>
            );
          })()}
          {(() => {
            const isExplore = getCurrentPage() === "explore";
            if (isExplore || idleMounted) exploreMountedRef.current = true;
            if (!exploreMountedRef.current) return null;
            return (
              <div style={isExplore ? undefined : { visibility: "hidden", opacity: 0, position: "absolute", top: 0, left: 0, right: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
                <MemoExplorePage {...commonProps} isVisible={isExplore} />
              </div>
            );
          })()}
          <RouteTransitionManager
            preloadedData={preloadedData}
            exploreDataReady={exploreDataReady}
            preloadExploreData={preloadExploreData}
          >
            <Routes>
              {/* Home and Library render nothing — handled by always-mounted divs above */}
              <Route path="/" element={null} />
              <Route
                path="/ref/:referrerId"
                element={<Suspense fallback={<PageLoader />}><ReferralPage {...commonProps} /></Suspense>}
              />

              <Route path="/explore" element={null} />
              <Route path="/workspace" element={null} />
              <Route
                path="/workspace/session/:sessionId"
                element={
                  <ProtectedRoute user={user}>
                    <LectureSessionWrapper generationQueue={generationQueue} />
                  </ProtectedRoute>
                }
              />
              {/* The Study page is gone, but its session URLs are live user data — every
                  project created before this change was linked, bookmarked and shared as
                  /study/session/<id>. Redirect rather than 404. */}
              <Route path="/study" element={<Navigate to="/workspace" replace />} />
              <Route path="/study/session/:sessionId" element={<StudySessionRedirect />} />
              <Route
                path="/homework/session/:sessionId"
                element={
                  <ProtectedRoute user={user}>
                    <HomeworkSessionWrapper generationQueue={generationQueue} />
                  </ProtectedRoute>
                }
              />
              <Route path="/passwordless" element={<Suspense fallback={<PageLoader />}><PasswordlessPage /></Suspense>} />
              <Route path="/verify" element={<VerifyEmailAction />} />
              {/* Nothing to render: the slug was banked at module load, so this only has to
                  put the visitor on the landing page. `replace` so Back does not return them
                  to a link that immediately redirects again. */}
              <Route path="/s/:source" element={<Navigate to="/" replace />} />
              {/* No element: getCurrentPage() maps "/landing" to "landing", and that branch
                  returns above <Routes> entirely. A duplicate element here would be dead code
                  that renders the page a second time INSIDE the app shell if the branch above
                  ever moved. The path is kept so the route is known and does not fall through
                  to the 404. */}
              <Route path="/landing" element={null} />

              {/* Create routes */}
              {/* element={null}, exactly like "/" above — these paths are SERVED BY THE
                  ALWAYS-MOUNTED HomePage at the top of this component, because
                  getCurrentPage() maps /create* to "home".
                  Rendering a <HomePage> here as well mounted the page TWICE: the always-
                  mounted copy computes hidden={getCurrentPage() !== "home"} -> false on
                  /create, so it stayed visible, and this route stacked a second full copy
                  under it (measured: two <h1>s, the second at y=3632). Both copies then
                  claimed view-transition-name "magi-composer" — the duplicate that
                  HomePage.jsx:906 warns "aborts the whole transition", killing the
                  Home -> Workspace morph, and creating a second stacking context that
                  scrambled the layout.
                  The defaultType prop the three sub-routes used to pass is NOT lost: it was
                  never read anywhere in HomePage. */}
              <Route path="/create" element={null} />
              <Route path="/create/course" element={null} />
              <Route path="/create/lesson" element={null} />
              <Route path="/create/problem" element={null} />

              {/* Legacy redirects */}
              <Route path="/library/*" element={<Navigate to="/workspace" replace />} />
              <Route path="/courses/*" element={<Navigate to="/workspace" replace />} />
              <Route path="/lessons/*" element={<Navigate to="/workspace" replace />} />
              <Route path="/problems/*" element={<Navigate to="/workspace" replace />} />
              <Route path="/projects" element={<Navigate to="/workspace" replace />} />
              <Route path="/quest" element={<Navigate to="/workspace" replace />} />
              <Route path="/homework" element={<Navigate to="/workspace" replace />} />
              <Route path="/lecture" element={<Navigate to="/workspace" replace />} />
              <Route path="/arena" element={<Navigate to="/workspace" replace />} />
              <Route path="/games" element={<Navigate to="/workspace" replace />} />

              <Route
                path="/search"
                element={
                  <ProtectedRoute user={user}>
                    <SearchResultsWrapper {...commonProps} />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/credits"
                element={
                  <ProtectedRoute user={user}>
                    <Suspense fallback={<PageLoader />}>
                      <PurchaseCreditsPage
                        {...commonProps}
                        refreshCredits={refreshCredits}
                      />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/credits/success"
                element={
                  <ProtectedRoute user={user}>
                    <Suspense fallback={<PageLoader />}>
                      <PurchaseSuccessPage
                        {...commonProps}
                        refreshCredits={refreshCredits}
                      />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute user={user}>
                    <Suspense fallback={<PageLoader />}>
                      <SettingsPage {...commonProps} />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/billing"
                element={
                  <ProtectedRoute user={user}>
                    <Suspense fallback={<PageLoader />}>
                      <BillingPage {...commonProps} />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/api"
                element={
                  <ProtectedRoute user={user}>
                    <Suspense fallback={<PageLoader />}>
                      <ApiPage {...commonProps} />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/contact"
                element={
                  <ProtectedRoute user={user}>
                    <Suspense fallback={<PageLoader />}>
                      <ContactPage {...commonProps} />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/video/:videoId"
                element={<VideoPlayerPageWrapper {...commonProps} />}

              />

              {/* A FEATURED COURSE IS A PROJECT AND OPENS AS ONE. `CoursePage` was a
                  read-only page of its own — title, description, a grid of lesson stills —
                  describing the same thing the project view already describes, and far less
                  of it. Explore links straight at the project now; this keeps the old url
                  working for anything already bookmarked or shared.
                  A plain redirect, not a wrapper: the ids are the same, because the project
                  kept the course's own document id when it was moved. */}
              <Route path="/course/:courseId" element={<CourseToProjectRedirect />} />

              {/* Admin routes - no ProtectedRoute needed, AdminPage handles its own auth */}
              <Route
                path="/admin"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminPage {...commonProps} />
                  </Suspense>
                }
              />
              {/* A full page of its own, not a modal over the dashboard: editing a reel is
                  a sitting-down task, and it needs a URL you can reopen and share. Auth is
                  the admin API's job — every call it makes is behind require_admin. */}
              <Route
                path="/admin/reel/:jobId"
                element={
                  <Suspense fallback={<PageLoader />}>
                    {/* The queue, because /admin/reel/new can LAUNCH. Without it the
                        composer's launch button reported nothing at all — no queue row, no
                        progress, no error — and the only way to learn a reel had started
                        was to navigate back to the dashboard. */}
                    <ReelEditorPage generationQueue={generationQueue} />
                  </Suspense>
                }
              />

              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </RouteTransitionManager>
          </main>
        </NavigationWrapper>

        {/* The floating queue widget is no longer rendered. Every surface that can start a
            job now shows its own progress in place — inline cards on Home, a progress dial on
            the Workspace card — so a pill in the corner duplicated that and covered content.

            The QUEUE ITSELF is untouched: useGenerationQueue still runs above and is still
            passed to the pages, so jobs are tracked, polled and surfaced exactly as before.
            Only this floating presentation is gone. */}

        {/* THE ONE THING A FAILURE SAYS OUT LOUD. The floating queue widget above was
            removed because every surface shows its own progress in place — which is true of
            progress and was NOT true of failure: a job that died took its card away and said
            nothing. This is not a progress pill; it only ever appears when something has
            gone wrong, and it waits to be dismissed rather than fading. */}
        <FailedJobBanner generationQueue={generationQueue} />

        {/* Debug Panel - Removed: Component no longer exists */}

        {/* Firebase Checker (development only) */}
        {import.meta.env.DEV && !user && <FirebaseChecker />}
      </div>

      {showSubscribe && (
        <Suspense fallback={null}>
          <SubscriptionModal user={user} onClose={() => setShowSubscribe(false)} />
        </Suspense>
      )}

      </>
      )}

      {/* OUTSIDE the ternary. It portals into <body>, so its position in the tree never
          mattered — only whether it mounts, and inside the shell branch it did not mount on
          the landing page. The landing page therefore grew its own copy of these links, where
          iubenda's badge styling survived: the CSS that strips it is scoped to #legal-footer,
          which only this component renders. Per-path exclusions live in NO_FOOTER_PATHS. */}
      <GlobalFooter />
    </>
  );
};

// Wrapper components to handle params
const CourseDetailPageWrapper = (props) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  return (
    <Suspense fallback={<PageLoader />}>
      <CourseDetailPage
        {...props}
        courseId={courseId}
        courseData={state?.courseData} // Pass preloaded course data to prevent flash
        setCurrentCourseId={props.setCurrentCourseId}
        setCourseRefreshCallback={props.setCourseRefreshCallback}
        setCurrentPage={(page, params) => {
          if (page === "courses") navigate("/courses");
          else if (page === "video-player" && params?.videoId)
            navigate(`/video/${params.videoId}`, { state: params });
        }}
      />
    </Suspense>
  );
};

/* Old project URLs. Every session created before the Study page was removed was linked
   as /study/session/<id>, so those links live in bookmarks, history and anywhere a user
   shared one. `replace` keeps them out of the back stack — a redirect the user can bounce
   back into is a trap, not a fix. */
const StudySessionRedirect = () => {
  const { sessionId } = useParams();
  return <Navigate to={`/workspace/session/${sessionId}`} replace />;
};

/* /course/<id> was the featured-course page. Those urls are in bookmarks and shared links,
   and the id is unchanged — the project kept the course's own document id — so this is a
   one-line redirect rather than anything that needs to resolve the course first. */
const CourseToProjectRedirect = () => {
  const { courseId } = useParams();
  return <Navigate to={`/workspace/session/${courseId}`} replace />;
};


const LectureSessionWrapper = ({ generationQueue }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const onBack = useCallback(() => navigate("/workspace"), [navigate]);
  return (
    <Suspense fallback={<PageLoader />}>
      <LectureSession sessionId={sessionId} onBack={onBack} generationQueue={generationQueue} />
    </Suspense>
  );
};

const HomeworkSessionWrapper = ({ generationQueue }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const onBack = useCallback(() => navigate("/workspace"), [navigate]);
  return (
    <Suspense fallback={<PageLoader />}>
      <HomeworkSession sessionId={sessionId} onBack={onBack} generationQueue={generationQueue} />
    </Suspense>
  );
};

const SearchResultsWrapper = (props) => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const navigate = useNavigate();

  return (
    <Suspense fallback={<PageLoader />}>
      <SearchResults
        {...props}
        searchQuery={query}
        setCurrentPage={(page, params) => {
          const routes = {
            generator: "/lessons/new",
            "course-generator": "/courses/new",
            "course-detail": `/courses/${params?.courseId}`,
            "video-player": `/video/${params?.videoId || "view"}`,
          };
          navigate(routes[page] || "/", { state: params });
        }}
      />
    </Suspense>
  );
};

const VideoPlayerPageWrapper = (props) => {
  const { videoId: rawVideoId } = useParams();
  const videoId = rawVideoId;
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const [fetchedData, setFetchedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const { user } = useAuth();
  const t = useT();

  // THE WALL, ON DEMAND. Watching is open (settings.WATCH_REQUIRES_ACCOUNT), so this path only
  // runs when that flag is back on — `?preview=locked` forces it for a signed-in account so the
  // wall can be looked at without flipping the server. It suppresses the unlock client-side
  // only; the file is not withheld, so it previews the wall rather than being one.
  const forceLocked = new URLSearchParams(location.search).get("preview") === "locked";

  // Anonymous counts as signed out here, exactly as it does in LoginGateProvider and in
  // media_router's /meta. One definition of "visitor" across all three, or they disagree about
  // who gets the wall.
  const isSignedOut = !user || user.isAnonymous;

  // If navigating from Library/Queue, state already has everything we need.
  // Only block rendering when there's no state data (direct URL / shared link).
  //
  // SIGNED-OUT VISITORS DO NOT GET THIS FAST PATH. `state.videoUrl` is handed over by whatever
  // linked here, and Explore builds it from a public payload — so honouring it started PLAYING
  // the video for the one person the gate exists for, for as long as /meta took to answer. The
  // shortcut stays for signed-in accounts, who are entitled to the file either way.
  const hasStateData = !!state?.videoUrl && !isSignedOut && !forceLocked;

  useEffect(() => {
    if (!videoId) return;

    if (!hasStateData) setIsLoading(true);
    setAccessError(null);
    let cancelled = false;

    apiService
      .makeRequest(`/api/media/video/${videoId}/meta`)
      .then((meta) => {
        if (cancelled) return;

        // Watching needs an account. The server withholds storage_path for signed-out
        // visitors, so there is no URL to build — show the wall and keep the title/thumbnail,
        // which is what makes a shared link worth tapping in the first place.
        if (meta.requires_signin || forceLocked) {
          setNeedsSignIn(true);
          setFetchedData({ title: meta.title });
          return;
        }

        // Direct Firebase Storage URLs when paths are available (no backend roundtrip).
        // signed_url is no longer sent (reels are public objects again, 2026-09-13) but still
        // wins when present, so an older cached response keeps playing.
        const vUrl = meta.signed_url
          || (meta.storage_path ? storageUrl(meta.storage_path) : apiService.getVideoUrl(videoId));
        const sUrl = meta.subtitle_storage_path ? storageUrl(meta.subtitle_storage_path) : apiService.getSubtitleUrl(videoId);
        setNeedsSignIn(false);
        setFetchedData({
          videoUrl: vUrl,
          subtitleUrl: sUrl,
          title: meta.title,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          if (err.status === 403) {
            setAccessError("This video is not yet available to the public.");
          } else if (err.status === 401) {
            setNeedsSignIn(true);
          } else if (!hasStateData) {
            // Deliberately NOT falling back to the streaming URL on an auth failure — that
            // endpoint is open, so doing so would walk straight around the wall we just hit.
            setAccessError("Couldn't load this video. Please try again.");
          }
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Re-runs when the viewer signs in, so the video starts on its own once they have an
    // account — no "now click play again" step after the wall.
  }, [videoId, user?.uid, user?.isAnonymous, forceLocked]);

  // Same rule as hasStateData above: a signed-out visitor's URL can only come from the gate's
  // own answer, never from navigation state.
  const videoUrl = (isSignedOut ? null : state?.videoUrl) || fetchedData?.videoUrl;
  const title = state?.title || fetchedData?.title;
  const description = state?.description || fetchedData?.description;
  const subtitleUrl = state?.subtitleUrl || fetchedData?.subtitleUrl;
  // Set document.title for SEO
  useEffect(() => {
    if (title) {
      document.title = `${title} | Magi`;
    }
    return () => {
      document.title = "Magi";
    };
  }, [title]);

  if (isLoading && !hasStateData) {
    return <PageLoader />;
  }

  if (accessError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
          color: "#fff",
          textAlign: "center",
          padding: "0 20px",
        }}
      >
        <h2 style={{ fontSize: "24px", marginBottom: "12px" }}>
          Access Restricted
        </h2>
        <p style={{ fontSize: "16px", color: "#aaa" }}>{accessError}</p>
      </div>
    );
  }

  // The wall, drawn as a locked player rather than a separate page: same place and shape as the
  // real player, so signing in swaps the cover for the video with no jump and no second click.
  //
  // The lesson's title goes ABOVE it, exactly where the player page puts it. Without it this
  // route rendered a single dark rectangle on an empty page — and the thing a shared link has
  // to answer first is "what am I being asked to sign in for".
  if (needsSignIn) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        {title && (
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{title}</h1>
        )}
        <LockedVideo
          videoId={videoId}
          title={title}
          // Straight from the queue means this is the video they just made, so the ask is
          // "watch YOURS", not the generic prompt for someone else's link.
          justCreated={state?.previousPage === "queue"}
          // No aspect-video: the sign-in form inside sets the height.
          className="w-full"
        />
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <VideoPlayerPage
        {...props}
        videoId={videoId}
        videoUrl={videoUrl}
        subtitleUrl={subtitleUrl}
        title={title}
        description={description}
        previousPage={state?.previousPage || "home"}
        searchQuery={state?.searchQuery || ""}
        setCurrentPage={(page) => {
          if (page === "home") navigate("/");
          else if (page === "generator") navigate("/lessons");
          else if (page === "help") navigate("/problems");
        }}
      />
    </Suspense>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <LoginGateProvider>
          <AppContent />
          <CookieBanner />
        </LoginGateProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
